import React, { useState, useCallback, useEffect, Component, type ErrorInfo, type ReactNode } from "react";
import "./index.css";
import { UploadScreen } from "./components/UploadScreen";
import { AnalyzingScreen } from "./components/AnalyzingScreen";
import { ResultScreen } from "./components/ResultScreen";
import { Sidebar } from "./components/Sidebar";
import { type AnalysisResult } from "./api/mockData";
import { analyzePhoto } from "./api/analyzeApi";
import { saveAnalysis, getHistory, getAnalysisById } from "./api/historyStore";

type AppScreen = "upload" | "analyzing" | "result";

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function getRouteResultId(): string | null {
  const hash = window.location.hash;
  const match = hash.match(/^#\/result\/(.+)$/);
  return match ? match[1] : null;
}

class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("App error:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, fontFamily: "Manrope, sans-serif" }}>
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>Что-то пошло не так</h2>
          <p style={{ fontSize: 14, color: "#666", marginBottom: 16 }}>
            {this.state.error?.message}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              padding: "10px 20px",
              background: "#333",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Попробовать снова
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppContent() {
  const [screen, setScreen] = useState<AppScreen>("upload");
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [photoDataUrls, setPhotoDataUrls] = useState<string[]>([]);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [history, setHistory] = useState<AnalysisResult[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Load history and check hash route on mount
  useEffect(() => {
    setHistory(getHistory());

    const routeId = getRouteResultId();
    if (routeId) {
      const saved = getAnalysisById(routeId);
      if (saved) {
        setResult(saved);
        setScreen("result");
      }
    }

    const onHashChange = () => {
      const id = getRouteResultId();
      if (id) {
        const saved = getAnalysisById(id);
        if (saved) {
          setResult(saved);
          setScreen("result");
        }
      } else {
        setScreen("upload");
        setResult(null);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const handlePhotosSelected = useCallback(async (files: File[]) => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPhotoUrls(urls);
    setScreen("analyzing");

    // Convert to data URLs for storage (in parallel)
    const dataUrls = await Promise.all(files.map(fileToDataUrl));
    setPhotoDataUrls(dataUrls);
  }, []);

  const handleAnalysisComplete = useCallback(async () => {
    try {
      // Analyse uploaded photos via Canvas-based computer vision pipeline.
      // In production this call is replaced by POST /api/analyze which runs
      // the same pipeline server-side through a trained AI/ML model.
      const analysisResult = await analyzePhoto(photoDataUrls);

      setResult(analysisResult);
      try {
        saveAnalysis(analysisResult);
        setHistory(getHistory());
        window.location.hash = `#/result/${analysisResult.id}`;
      } catch (saveErr) {
        console.error("Error saving analysis:", saveErr);
      }
      setScreen("result");
    } catch (err) {
      console.error("Photo analysis failed:", err);
      // Fallback — retry with whatever data we have
      try {
        const fallback = await analyzePhoto(
          photoDataUrls.length > 0 ? photoDataUrls : []
        );
        setResult(fallback);
        setScreen("result");
      } catch {
        // If even the fallback fails, stay on analysing screen is bad UX —
        // go back to upload so the user can try again.
        setScreen("upload");
      }
    }
  }, [photoDataUrls]);

  const handleReset = useCallback(() => {
    photoUrls.forEach((u) => URL.revokeObjectURL(u));
    setPhotoUrls([]);
    setPhotoDataUrls([]);
    setResult(null);
    window.location.hash = "";
    setHistory(getHistory());
    setScreen("upload");
  }, [photoUrls]);

  const handleOpenResult = useCallback((id: string) => {
    const saved = getAnalysisById(id);
    if (saved) {
      setResult(saved);
      window.location.hash = `#/result/${id}`;
      setScreen("result");
    }
  }, []);

  return (
    <main className="w-full">
      {/* Hamburger menu button */}
      {screen === "upload" && (
        <button
          className="hamburger-btn"
          onClick={() => setSidebarOpen(true)}
          aria-label="Открыть меню"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M3 5.5H17" stroke="var(--foreground)" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M3 10H17" stroke="var(--foreground)" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M3 14.5H12" stroke="var(--foreground)" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      )}

      {/* Sidebar — only available on the upload/main screen */}
      {screen === "upload" && (
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          history={history}
          onNewAnalysis={handleReset}
          onOpenResult={(id) => {
            handleOpenResult(id);
            setSidebarOpen(false);
          }}
        />
      )}

      {screen === "upload" && (
        <UploadScreen
          onPhotosSelected={handlePhotosSelected}
          history={history}
          onOpenResult={handleOpenResult}
        />
      )}
      {screen === "analyzing" && (
        <AnalyzingScreen
          photoUrls={photoUrls.length > 0 ? photoUrls : photoDataUrls}
          onComplete={handleAnalysisComplete}
        />
      )}
      {screen === "result" && result && (
        <ResultScreen
          result={result}
          onReset={handleReset}
        />
      )}
    </main>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}

export default App;
